import { DateTime, Interval } from 'luxon';
import { cast, getParentOfType, types } from 'mobx-state-tree';
import type { Instance } from 'mobx-state-tree/dist/core/type/type';

import { AdmissionPeriodType } from '@/enums/admissions';
import { ApplicationModel } from '@/models/applications';
import { edges, nullOrUndefined } from '@/models/utils';
import { BaseModel } from '@/stores/shared';
import { StudentCalendarStore } from '@/stores/StudentCalendarStore';
import { DateHelpers } from '@/utils/dateTime';
import { AdmissionHelpers } from '@/utils/internships';
import type { MentorProfileModelType, UserModelType } from '@/models/users';
import type { WeekBeginEnd } from '@/types/common';
import type { Edge } from '@/types/shared';

export const ScheduledInterviewModel = types.compose(
  BaseModel,
  types.model({
    summary: types.maybe(types.string),
    confLink: types.maybe(types.string),
    application: types.late(() => types.maybe(ApplicationModel)),
    slot: types.late(() => types.maybe(SlotModel)),
  }),
);

export type ScheduledInterviewModelType = Instance<
  typeof ScheduledInterviewModel
> & {};

export const SlotModel = types.compose(
  BaseModel,
  types
    .model({
      // TODO: replace start with begin across all the models/types
      start: types.string,
      end: types.string,
      deletable: types.maybe(types.boolean),
      interview: nullOrUndefined(ScheduledInterviewModel),
    })
    .views((self) => ({
      get tz(): string | undefined {
        const parent = getParentOfType(self, StudentCalendarStore);
        return parent.tz;
      },
    }))
    .views((self) => ({
      get beginDt() {
        return DateTime.fromISO(self.start, { zone: self.tz });
      },
      get endDt() {
        return DateTime.fromISO(self.end, { zone: self.tz });
      },
    }))
    .actions((self) => ({
      setInterview(interview: ScheduledInterviewModelType) {
        self.interview = interview;
      },
    })),
);

export type SlotModelType = Instance<typeof SlotModel> & {};

export const GoogleCalendarEventModel = types.model({
  summary: types.maybe(types.string),
  start: types.maybe(types.string),
  end: types.maybe(types.string),
});

export type GoogleCalendarEventModelType = Instance<
  typeof GoogleCalendarEventModel
> & {};

export const CandidateApplicationInterviewModel = types
  .model({
    application: types.late(() => ApplicationModel),
    scheduledInterview: nullOrUndefined(ScheduledInterviewModel),
    slots: types.maybe(edges(SlotModel)),
    selectedWeekIndex: 0,
  })
  .views((self) => ({
    get weeks(): WeekBeginEnd[] {
      const admissions = self.application.project?.season;
      const tz = self.application.candidate?.studentprofile.tz;

      if (!admissions || !tz) {
        return [];
      }

      const mentorProfiles: MentorProfileModelType[] =
        self.application.project?.mentors?.edges.map(
          (e: Edge<UserModelType>) => e.node.mentorprofile,
        );

      // Select all extended deadlines from mentor profiles and convert them
      // to DateTime objects to further select the latest one
      const extendedDeadlinesDt = mentorProfiles
        .filter((mp) => !!mp.extendedActionsDeadline)
        .map((mp) =>
          DateTime.fromISO(mp.extendedActionsDeadline!, {
            zone: tz,
          }),
        )
        .filter((dt) => dt.isValid);

      const maxDeadlineDt = DateTime.max(...extendedDeadlinesDt);

      const interviewPeriod = AdmissionHelpers.getAdmissionPeriodByType(
        self.application.project.season,
        AdmissionPeriodType.INTERVIEW,
      );

      if (!interviewPeriod || !interviewPeriod.start || !interviewPeriod.end) {
        return [];
      }

      const endDeadline = maxDeadlineDt
        ? DateTime.max(
            DateTime.fromISO(interviewPeriod.end, { zone: tz }),
            maxDeadlineDt,
          ).toISO()!
        : interviewPeriod.end;

      return DateHelpers.convertDateRangeToWeeks(
        DateTime.now().toISO(),
        endDeadline,
        tz,
      );
    },
    getSlotsForWeek(week: WeekBeginEnd): SlotModelType[] {
      const filterSlot = (s: SlotModelType) => {
        const interval = Interval.fromDateTimes(week.begin, week.end);
        return interval.contains(s.beginDt);
      };

      return (
        self.slots?.edges
          .map((e) => e.node)
          .filter(filterSlot)
          .sort((a, b) => (a.beginDt < b.beginDt ? -1 : 1)) || []
      );
    },
  }))
  .views((self) => ({
    get nonEmptyWeeks(): WeekBeginEnd[] {
      return self.weeks.filter((w) => self.getSlotsForWeek(w).length > 0);
    },
  }))
  .views((self) => ({
    get activeWeek(): WeekBeginEnd | null {
      if (self.selectedWeekIndex === undefined) {
        return null;
      }

      return self.nonEmptyWeeks[self.selectedWeekIndex];
    },
  }))
  .actions((self) => ({
    setSelectedWeekIndex(index: number) {
      self.selectedWeekIndex = index;
    },
    setSelectedWeek(week: WeekBeginEnd) {
      self.selectedWeekIndex = self.nonEmptyWeeks.indexOf(week);
    },
    setScheduledInterview(interview: ScheduledInterviewModelType) {
      self.scheduledInterview = interview;
    },
    removeSlot(slot: Instance<typeof SlotModel>) {
      if (!self.slots) {
        return;
      }

      self.slots.edges = cast(
        self.slots.edges.filter((e) => e.node.pk !== slot.pk),
      );
    },
  }));

export type CandidateApplicationInterviewModelType = Instance<
  typeof CandidateApplicationInterviewModel
> & {};

export const MentorCalendarModel = types.model({
  startDate: types.string,
  endDate: types.string,
  googleCalendarEnabled: types.boolean,
  canManageSlots: types.boolean,
  invitedForInterviewCount: types.number,
  slots: edges(SlotModel),
  googleCalendarEvents: edges(GoogleCalendarEventModel),
});

export type MentorCalendarModelType = Instance<typeof MentorCalendarModel>;
